import { useState } from "react";
import type { ThemeClasses } from "../types";
import { useVehiclesSocketStore } from "../../../../../../store/admin/vehiclesSocketStore";
import {
  barridoFileTitle,
  downloadBarrido,
  fetchDevicesForBarrido,
} from "../../../../../../features/devices/barrido";
import { getError } from "../../../../../../utils/getError";

interface DeviceBarridoModalProps {
  companyId: string;
  companyName: string;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onClose: () => void;
  t: (key: string, options?: any) => string;
}

const COLUMN_KEYS = [
  "imei",
  "unitName",
  "deviceName",
  "model",
  "lastReport",
] as const;

export function DeviceBarridoModal({
  companyId,
  companyName,
  isDarkMode,
  themeClasses,
  onClose,
  t,
}: DeviceBarridoModalProps) {
  const isLoadingInitialPositions = useVehiclesSocketStore(
    (state) => state.isLoadingInitialPositions,
  );
  const isConnected = useVehiclesSocketStore((state) => state.isConnected);
  const socketLoading = useVehiclesSocketStore((state) => state.loading);
  const lastInitializedCompanyId = useVehiclesSocketStore(
    (state) => state.lastInitializedCompanyId,
  );

  const positionsPending =
    isLoadingInitialPositions ||
    socketLoading ||
    lastInitializedCompanyId !== companyId;

  const [generating, setGenerating] = useState(false);
  const [downloaded, setDownloaded] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const muted = isDarkMode ? "text-gray-300" : "text-gray-500";
  const infoBox = isDarkMode
    ? "border-primary-500/30 bg-primary-500/10 text-primary-200"
    : "border-primary-300 bg-primary-50 text-primary-700";
  const warnBox = isDarkMode
    ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
    : "border-amber-300 bg-amber-50 text-amber-800";
  const dangerBox = isDarkMode
    ? "border-red-500/30 bg-red-500/10 text-red-200"
    : "border-red-300 bg-red-50 text-red-800";

  const fileTitle = barridoFileTitle(t("devices.barrido.fileTitle"), companyName);
  const blocked = positionsPending || generating;

  const handleDownload = async () => {
    if (blocked) return;
    setGenerating(true);
    setError(null);
    try {
      const devices = await fetchDevicesForBarrido(companyId);
      const total = await downloadBarrido(devices, {
        headers: COLUMN_KEYS.map((key) => t(`devices.barrido.headers.${key}`)),
        empty: {
          noVehicle: t("devices.barrido.empty.noVehicle"),
          unitName: t("devices.barrido.empty.unitName"),
          deviceName: t("devices.barrido.empty.deviceName"),
          model: t("devices.barrido.empty.model"),
          lastReport: t("devices.barrido.empty.lastReport"),
        },
        fileTitle,
        sheetName: t("devices.barrido.sheetName"),
      });
      setDownloaded(total);
    } catch (err: unknown) {
      setError(getError(err) || t("devices.barrido.error"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onClick={generating ? undefined : onClose}
    >
      <div
        className={`max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border p-6 shadow-xl ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={`text-lg font-bold ${themeClasses.text}`}>
          {t("devices.barrido.title")}
        </h2>
        <p className={`mt-1 text-sm ${muted}`}>
          {t("devices.barrido.intro", { company: companyName })}
        </p>

        <ul className={`mt-3 space-y-1 text-sm ${themeClasses.text}`}>
          {COLUMN_KEYS.map((key) => (
            <li key={key} className="flex items-center gap-2">
              <i className="ri-check-line text-primary-500" />
              <span>{t(`devices.barrido.columns.${key}`)}</span>
            </li>
          ))}
        </ul>

        <div className={`mt-4 rounded-lg border px-3 py-2 text-xs ${warnBox}`}>
          {t("devices.barrido.sgNotice")}
        </div>

        <p className={`mt-3 text-xs ${muted}`}>
          {t("devices.barrido.fileName", { file: `${fileTitle}.xlsx` })}
        </p>

        {positionsPending && (
          <div
            className={`mt-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${infoBox}`}
          >
            <i className="ri-loader-4-line animate-spin" />
            <span>{t("devices.barrido.loadingPositions")}</span>
          </div>
        )}

        {!positionsPending && !isConnected && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${warnBox}`}>
            {t("devices.barrido.socketDown")}
          </div>
        )}

        {downloaded !== null && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${infoBox}`}>
            {t("devices.barrido.downloaded", { count: downloaded })}
          </div>
        )}

        {error && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${dangerBox}`}>
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={generating}
            className={`rounded-lg px-3 py-2 text-sm font-medium ${
              isDarkMode
                ? "text-gray-300 hover:bg-white/5"
                : "text-gray-600 hover:bg-gray-100"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {t("devices.barrido.close")}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={blocked}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i
              className={
                blocked
                  ? "ri-loader-4-line animate-spin"
                  : "ri-download-2-line"
              }
            />
            <span>
              {positionsPending
                ? t("devices.barrido.loadingPositionsShort")
                : generating
                  ? t("devices.barrido.generating")
                  : t("devices.barrido.download")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
